import { Component, Input, Output, EventEmitter, TemplateRef, ContentChild, OnDestroy } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { CdkDragDrop, moveItemInArray, CdkDragEnter, CdkDragExit } from '@angular/cdk/drag-drop';

export interface TreeNode {
  id: number;
  name: string;
  children?: TreeNode[];
  [key: string]: any;
}

@Component({
    selector: 'app-custom-tree',
    templateUrl: './custom-tree.component.html',
    styleUrls: ['./custom-tree.component.scss'],
    animations: [
        trigger('expandCollapse', [
            transition(':enter', [
                style({ height: 0, opacity: 0 }),
                animate('200ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                style({ height: '*', opacity: 1 }),
                animate('200ms ease-in', style({ height: 0, opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class CustomTreeComponent implements OnDestroy {
  @Input() data: TreeNode[] = [];
  @Input() showLine = true;
  @Input() expandAll = false;
  @Input() title = '路径';
  @Input() showActions = true;
  @Input() enableDragSort = true; // 默认启用拖拽排序
  @Input() keepStatus = true; // 默认启用拖拽排序

  @Output() editNode = new EventEmitter<TreeNode>();
  @Output() addChild = new EventEmitter<TreeNode>();
  @Output() nodeDrop = new EventEmitter<{event: CdkDragDrop<TreeNode[]>, level: number, parentId?: number}>();

  @ContentChild('actionTemplate', { static: false }) actionTemplate?: TemplateRef<any>;

  @Input() expandedNodes = new Set<number>();
  dragDropLists = new Map<string, any>(); // 存储不同层级的拖拽列表
  invalidDropContainers = new Set<string>(); // 存储无效拖拽的容器

  ngOnInit() {
    this.initializeExpandedNodes();
  }

  ngOnChanges() {
    this.initializeExpandedNodes();
  }

  private initializeExpandedNodes() {
    this.initOpenCloseStatus();
    if (this.expandAll && this.data) {
      // 使用setTimeout来避免ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        this.expandAllNodes(this.data);
      });
    }
  }

  initOpenCloseStatus() {
    const cacheStatus = window.localStorage.getItem(window.location.hash);
    if (cacheStatus) {
      try {
        const nodesId = JSON.parse(cacheStatus);
        this.expandedNodes = new Set(nodesId);
      } catch (e) {

      }
    }
  }

  private expandAllNodes(nodes: TreeNode[] | undefined) {
    if (!nodes || !Array.isArray(nodes)) { return; }

    nodes.forEach(node => {
      this.openNode(node.id);
      if (node.children && node.children.length > 0) {
        this.expandAllNodes(node.children);
      }
    });
  }

  toggleNode(node: TreeNode, event?: CdkDragDrop<TreeNode>) {
    if (!node) {
      return;
    }
    if (this.expandedNodes.has(node.id)) {
      this.closeNode(node.id, event);
    } else {
      this.openNode(node.id, event);
    }
  }

  openNode(nodeId: number, event?: CdkDragDrop<TreeNode>) {
    this.expandedNodes.add(nodeId);
    if (event && this.keepStatus) {
      window.localStorage.setItem(window.location.hash, JSON.stringify(Array.from(this.expandedNodes)));
    }
  }
  closeNode(nodeId: number, event?: CdkDragDrop<TreeNode>) {
    this.expandedNodes.delete(nodeId);
    if (event && this.keepStatus) {
      window.localStorage.setItem(window.location.hash, JSON.stringify(Array.from(this.expandedNodes)));
    }
  }

  isExpanded(node: TreeNode): boolean {
    if (!node) { return false; }
    return this.expandedNodes.has(node.id);
  }

  hasChildren(node: TreeNode): boolean {
    if (!node) { return false; }
    return !!(node.children && node.children.length > 0);
  }

  onEditNode(node: TreeNode, event: Event) {
    event.stopPropagation();
    this.editNode.emit(node);
  }

  onAddChild(node: TreeNode, event: Event) {
    event.stopPropagation();
    this.addChild.emit(node);
  }

  getIndentLevel(level: number): string {
    return `${level * 20}px`;
  }

  // 生成拖拽列表的唯一标识符
  getDropListId(parentId?: number): string {
    return parentId ? `level-${parentId}` : 'root-level';
  }

  // 验证拖拽是否有效（同级别排序）
  canDrop(event: CdkDragDrop<TreeNode[]> | CdkDragEnter<TreeNode[]>): boolean {
    const draggedNode = event.item.data as TreeNode;

    // 如果是根级别拖拽
    if (event.container.id === 'root-level') {
      // 只有根节点才能拖拽到根级别
      return !this.hasParent(draggedNode);
    }

    // 如果是子级别拖拽，检查是否具有相同的父节点
    const targetParentId = this.getParentIdFromContainerId(event.container.id);
    const draggedParentId = this.findParentId(draggedNode);

    return targetParentId === draggedParentId;
  }

  // 检查节点是否有父节点
  private hasParent(node: TreeNode): boolean {
    return this.findParentId(node) !== null;
  }

  // 从容器ID中获取父节点ID
  private getParentIdFromContainerId(containerId: string): number | null {
    if (containerId === 'root-level') {
      return null;
    }
    const match = containerId.match(/level-(\d+)/);
    return match ? parseInt(match[1]) : null;
  }

  // 查找节点的父节点ID
  private findParentId(node: TreeNode, nodes: TreeNode[] = this.data): number | null {
    for (const parentNode of nodes) {
      if (parentNode.children && parentNode.children.some(child => child.id === node.id)) {
        return parentNode.id;
      }
      if (parentNode.children) {
        const result = this.findParentId(node, parentNode.children);
        if (result !== null) {
          return result;
        }
      }
    }
    return null;
  }

  onDrop(event: CdkDragDrop<TreeNode[]>, level: number, parentId?: number) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    // 验证拖拽是否有效
    if (!this.canDrop(event)) {
      console.log('拖拽无效：只能在同一级别内排序');
      return;
    }

    console.log('拖拽事件:', event, '层级:', level, '父节点ID:', parentId);

    // 移动数组中的元素
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

    // 触发事件
    this.nodeDrop.emit({ event, level, parentId });
  }

  // 拖拽进入事件
  onDragEnter(event: CdkDragEnter<TreeNode[]>) {
    const containerId = event.container.id;
    if (!this.canDrop(event)) {
      this.invalidDropContainers.add(containerId);
      if (event.container.element && event.container.element.nativeElement) {
        event.container.element.nativeElement.classList.add('invalid-drop');
      }
    }
  }

  // 拖拽离开事件
  onDragExit(event: CdkDragExit<TreeNode[]>) {
    const containerId = event.container.id;
    this.invalidDropContainers.delete(containerId);
    if (event.container.element && event.container.element.nativeElement) {
      event.container.element.nativeElement.classList.remove('invalid-drop');
    }
  }

  // 检查容器是否为无效拖拽状态
  isInvalidDrop(containerId: string): boolean {
    return this.invalidDropContainers.has(containerId);
  }

  ngOnDestroy() {
    // 清理无效拖拽状态
    this.invalidDropContainers.clear();
  }
}
